import { Link } from 'react-router-dom';
import { useState } from 'react';
import { allArticles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';

const categoryNameMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryNameMap[c.slug] = c.name;
});

export default function Home() {
  const featuredArticles = allArticles.filter((a) => a.featured);
  const recentArticles = allArticles.filter((a) => !a.featured).slice(0, 6);
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setNewsletterStatus('success');
      return;
    }

    const formData = new FormData(form);
    const submitUrl = 'https://readdy.ai/api/form/d9q2mvue3oq4jqumtfkg';

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (response.ok && parsed.code === 'OK') {
        setNewsletterStatus('success');
        setNewsletterError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '订阅失败，请稍后再试';
        setNewsletterError(serverMsg);
        setNewsletterStatus('error');
      }
    } catch {
      setNewsletterError('网络错误，请检查连接后重试');
      setNewsletterStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="暗网51 | 深度解析与安全防护指南 - 权威暗网安全资讯平台"
        description="暗网51是专注于暗网生态深度解析的权威资讯平台，涵盖暗网安全威胁分析、隐私防护指南、网络安全知识科普与最新动态，帮助用户了解暗网风险、掌握自我防护技能，守护个人数据与网络安全。"
        keywords="暗网51,暗网安全防护,暗网风险解析,网络隐私保护,暗网最新动态,数据泄露防范,网络安全知识"
        canonicalUrl="https://www.darkweb51.com/"
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20digital%20security%20landscape%20with%20geometric%20shield%20patterns%2C%20subtle%20green%20teal%20and%20warm%20gold%20glowing%20accents%2C%20minimalist%20cybersecurity%20concept%2C%20clean%20professional%20dark%20background%20with%20network%20nodes%2C%20high%20quality%20editorial%20style&width=1800&height=900&seq=hero-2025-darkweb&orientation=landscape"
            alt="暗网51 - 深度解析与安全防护指南"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-4 md:px-6 w-full max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
              {siteInfo.name}
              <span className="block text-lg md:text-2xl font-normal text-white/80 mt-2">
                {siteInfo.tagline}
              </span>
            </h1>
            <p className="text-sm md:text-lg text-white/70 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              专注于暗网生态深度解析的权威资讯平台，涵盖安全威胁分析、隐私防护指南与网络安全知识，帮助您了解暗网风险、掌握自我防护技能，守护个人数据与网络安全。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/cyber-knowledge"
                className="px-6 py-3 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
              >
                开始学习
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-white/15 text-white rounded-md font-medium text-sm hover:bg-white/25 transition-colors no-underline whitespace-nowrap backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
              内容分类
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              六大核心板块，覆盖暗网安全的各个维度
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-all duration-300 no-underline"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-lg mb-4 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-3">
                  {cat.description}
                </p>
                <span className="text-xs text-foreground-400">
                  {cat.count} 篇文章
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8 md:mb-10">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                  精选文章
                </h2>
                <p className="text-sm text-foreground-500">编辑推荐的深度好文</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard
                  key={article.slug}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  category={article.category}
                  categoryName={categoryNameMap[article.category] || article.category}
                  author={article.author}
                  date={article.date}
                  readTime={article.readTime}
                  imageUrl={article.imageUrl}
                  tags={article.tags}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                最新文章
              </h2>
              <p className="text-sm text-foreground-500">持续更新的安全知识</p>
            </div>
            <Link
              to="/category/cyber-knowledge"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium no-underline whitespace-nowrap flex items-center gap-1"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {recentArticles.map((article) => (
              <ArticleCard
                key={article.slug}
                slug={article.slug}
                title={article.title}
                excerpt={article.excerpt}
                category={article.category}
                categoryName={categoryNameMap[article.category] || article.category}
                author={article.author}
                date={article.date}
                readTime={article.readTime}
                imageUrl={article.imageUrl}
                tags={article.tags}
              />
            ))}
          </div>
        </section>

        <section className="bg-foreground-950 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-white mb-3">
              加入我们的安全社区
            </h2>
            <p className="text-sm md:text-base text-foreground-300 max-w-lg mx-auto mb-8">
              订阅暗网51的新闻通讯，获取最新的网络安全资讯和深度分析
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
              data-readdy-form="newsletter"
            >
              <input
                type="text"
                name="company_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <input
                type="email"
                name="email"
                placeholder="请输入您的邮箱地址"
                required
                className="w-full sm:flex-1 px-4 py-3 bg-foreground-800 border border-foreground-700 rounded-md text-sm text-white placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                立即订阅
              </button>
              {newsletterStatus === 'success' && (
                <p className="text-sm text-primary-400 text-center w-full">感谢订阅！我们会将最新资讯发送到您的邮箱。</p>
              )}
              {newsletterStatus === 'error' && (
                <p className="text-sm text-accent-400 text-center w-full">{newsletterError || '订阅失败，请稍后再试'}</p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '暗网51',
            url: 'https://www.darkweb51.com/',
            description: siteInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.darkweb51.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '暗网51',
            url: 'https://www.darkweb51.com/',
            description: siteInfo.description,
            email: siteInfo.email,
            address: {
              '@type': 'PostalAddress',
              addressLocality: '北京',
              addressCountry: 'CN',
            },
          }),
        }}
      />
    </>
  );
}